/**
 * Mirrors the mobile `app/theme/palettes.ts` definitions so the salon-owner
 * web app can render previews of how each palette renders in the client app.
 * Keep these two files in sync.
 */

export type PaletteKey =
  | "rose"
  | "sage"
  | "lavender"
  | "sand"
  | "peach"
  | "midnight";

export type Palette = {
  name: string;
  desc: string;
  bg: string;
  surface: string;
  surfaceAlt: string;
  primary: string;
  primaryDark: string;
  primaryInk: string;
  secondary: string;
  accent: string;
  ink: string;
  inkSoft: string;
  line: string;
  isDark: boolean;
};

export const palettes: Record<PaletteKey, Palette> = {
  rose: {
    name: "Rose Boudoir",
    desc: "Dusty rose + sage. Klasyczny, ciepły, kobiecy.",
    bg: "#f7f1ec",
    surface: "#ffffff",
    surfaceAlt: "#f1e5dc",
    primary: "#a64560",
    primaryDark: "#8a3450",
    primaryInk: "#ffffff",
    secondary: "#8a9a7b",
    accent: "#d8a48f",
    ink: "#2a1f24",
    inkSoft: "#75636b",
    line: "rgba(42,31,36,0.08)",
    isDark: false,
  },
  sage: {
    name: "Sage Garden",
    desc: "Zielona szałwia + brzoskwinia. Spokojny, naturalny.",
    bg: "#f4f3ee",
    surface: "#ffffff",
    surfaceAlt: "#e8eadf",
    primary: "#5a6b4a",
    primaryDark: "#43523a",
    primaryInk: "#ffffff",
    secondary: "#c89878",
    accent: "#a89c7e",
    ink: "#23271f",
    inkSoft: "#646a5e",
    line: "rgba(35,39,31,0.08)",
    isDark: false,
  },
  lavender: {
    name: "Lavender Mist",
    desc: "Lawenda + perłowy szary. Eteryczny, delikatny.",
    bg: "#f5f2f7",
    surface: "#ffffff",
    surfaceAlt: "#ebe5f0",
    primary: "#7a5e9a",
    primaryDark: "#5e4878",
    primaryInk: "#ffffff",
    secondary: "#b8a8c8",
    accent: "#d4b8a0",
    ink: "#2a2230",
    inkSoft: "#6a5e7a",
    line: "rgba(42,34,48,0.08)",
    isDark: false,
  },
  sand: {
    name: "Sand Stone",
    desc: "Beż + terakota + burgund. Boho, ciepły.",
    bg: "#f5efe6",
    surface: "#ffffff",
    surfaceAlt: "#ebe0d0",
    primary: "#9a4d3a",
    primaryDark: "#7a3a2a",
    primaryInk: "#ffffff",
    secondary: "#c89878",
    accent: "#a8826a",
    ink: "#2e2419",
    inkSoft: "#6e5d48",
    line: "rgba(46,36,25,0.10)",
    isDark: false,
  },
  peach: {
    name: "Peach Sorbet",
    desc: "Brzoskwinia + koral + mięta. Słodki, młodzieżowy.",
    bg: "#fef0e8",
    surface: "#ffffff",
    surfaceAlt: "#ffe2d8",
    primary: "#e8755a",
    primaryDark: "#c25a44",
    primaryInk: "#ffffff",
    secondary: "#7ec4a8",
    accent: "#f4c95d",
    ink: "#3a1a1a",
    inkSoft: "#7a5550",
    line: "rgba(58,26,26,0.08)",
    isDark: false,
  },
  midnight: {
    name: "Midnight Bloom",
    desc: "Głęboki burgund + cream. Wieczorny, intymny, premium.",
    bg: "#2a1820",
    surface: "#3a2330",
    surfaceAlt: "#4a2d3c",
    primary: "#e8a87c",
    primaryDark: "#d49060",
    primaryInk: "#2a1820",
    secondary: "#c89876",
    accent: "#d4677e",
    ink: "#f7e8d8",
    inkSoft: "#c4a698",
    line: "rgba(247,232,216,0.12)",
    isDark: true,
  },
};

export const PALETTE_KEYS: PaletteKey[] = [
  "rose",
  "sage",
  "lavender",
  "sand",
  "peach",
  "midnight",
];
